import { useMutation } from '@apollo/client';
import type { InsertCustomRoleMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertCustomRoleDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { CreateCustomRoleTRPCInput } from './useCreateCustomRoleTRPC';
import { useCreateCustomRoleTRPC } from './useCreateCustomRoleTRPC';

/**
 * Flag-gated wrapper. Consumers ALWAYS import this hook (never the
 * `*TRPC` sibling directly). When the `trpc` flag is on, the call is
 * routed to the tRPC mutation; otherwise it falls back to the legacy
 * Apollo `customRoleInsert` GraphQL action so deployments without the
 * flag continue to function.
 */
export const useCreateCustomRole = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // Apollo mutation — legacy fallback. Evict the cached custom_role
  // list so the settings tab re-fetches after a create.
  const [createCustomRoleGraphQL, graphqlState] = useMutation(
    InsertCustomRoleDocument,
    {
      update: (cache) => {
        evictField(cache, 'custom_role');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useCreateCustomRoleTRPC();

  const createCustomRole = async (
    variables: CreateCustomRoleTRPCInput
  ): Promise<InsertCustomRoleMutation> => {
    if (trpcEnabled) {
      return trpcMutation.createCustomRole(variables);
    }

    const result = await createCustomRoleGraphQL({
      variables: {
        input: {
          Name: variables.name,
          Description: variables.description ?? undefined,
          RoleKeys: variables.roleKeys,
          UserIds: variables.userIds,
        },
      },
    });
    if (!result.data) {
      throw new Error('Failed to create custom role');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      createCustomRole,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    createCustomRole,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
